import { fetchStrapi, getBrandSlug } from "@/lib/strapi";

export type InstagramMediaPost = {
  key: string;
  src: string;
  href: string | null;
  alt: string;
};

export type NormalizeInstagramMediaOptions = {
  limit?: number;
};

export type GetInstagramMediaOptions = {
  brandSlug?: string;
  limit?: number;
  path?: string;
  init?: RequestInit;
};

export function buildInstagramMediaPath(brandSlug: string): string {
  return `/brands/${encodeURIComponent(brandSlug)}/instagram-media`;
}

function pickString(input: unknown, keys: string[]): string | null {
  if (!input || typeof input !== "object") return null;
  const record = input as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

export function normalizeInstagramMediaPayload(
  payload: unknown,
  options?: NormalizeInstagramMediaOptions,
): InstagramMediaPost[] {
  const limit = options?.limit ?? 4;

  const rawItems: unknown[] = (() => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return [];
    const record = payload as Record<string, unknown>;
    const data = record.data;
    if (Array.isArray(data)) return data;
    const items = record.items;
    if (Array.isArray(items)) return items;
    return [];
  })();

  return rawItems
    .map((item, index) => {
      const idValue = (item as { id?: unknown } | null)?.id;
      const id = typeof idValue === "string" || typeof idValue === "number" ? String(idValue) : String(index);

      const mediaUrl = pickString(item, ["thumbnail_url", "media_url", "image_url", "url", "src"]);
      if (!mediaUrl) return null;

      const href = pickString(item, ["permalink", "link", "href"]);
      const caption = pickString(item, ["caption", "title", "alt"]);
      const alt = caption ? caption : `Instagram post ${index + 1}`;
      return { key: `${id}-${index}`, src: mediaUrl, href: href ?? null, alt };
    })
    .filter((item): item is InstagramMediaPost => Boolean(item))
    .slice(0, limit);
}

export async function getInstagramMediaFromStrapi(options?: GetInstagramMediaOptions): Promise<InstagramMediaPost[]> {
  const brandSlug = options?.brandSlug ?? getBrandSlug();
  const path = options?.path ?? buildInstagramMediaPath(brandSlug);
  const payload = await fetchStrapi<unknown>(path, { init: options?.init, withBrandFilter: false });
  return normalizeInstagramMediaPayload(payload, { limit: options?.limit });
}

